import { useCallback, useMemo } from 'react';
import {
    capitalize,
    Checkbox,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    SelectChangeEvent
} from '@mui/material';
import { Check } from '@mui/icons-material';
import { useTheme } from '@dh/hooks';
import { LabelLarge } from '@dh/styles';

import { EllipsisText, FlexWrapper, MultiSelectBaseSelect } from './styles';

type MultiSelectOption = {
    id: string;
    label: string;
    isSelected: boolean;
    secondaryContent?: React.ReactNode;
    primaryFontWeight?: number;
};

type MultiSelectProps = {
    /**
     * All possible selectable options
     */
    readonly allOptions: MultiSelectOption[];
    name: string;
    /**
     * Localized text shown when no value is selected
     */
    placeHolder: string;
    onChange: (selectedIDs: string[]) => void;
    disabled?: boolean;
    /**
     * Keeps placeholder in renderValue
     */
    shouldKeepPlaceholder?: boolean;
    noMaxWidth?: boolean;
};

const MultiSelect = ({
    allOptions,
    name,
    placeHolder,
    onChange,
    disabled = false,
    shouldKeepPlaceholder = false,
    noMaxWidth = false
}: MultiSelectProps) => {
    const theme = useTheme();
    const inputLabelId = `multiselect-for-${name}`;

    // Memoize selected options to avoid recalculation on every render
    const selectedOptions = useMemo(
        () => allOptions.filter((value) => value.isSelected).map((value) => value.id),
        [allOptions]
    );

    // Memoize isSelected check
    const isSelected = useMemo(() => !!allOptions.find((value) => value.isSelected), [allOptions]);

    // Memoize checkbox styles
    const checkboxStyles = useMemo(
        () => ({ color: theme.colors.onSurfaceVariant, mt: 0.5 }),
        [theme.colors.onSurfaceVariant]
    );

    // Memoize check icon styles
    const checkIconStyles = useMemo(() => ({ marginRight: theme.spacing(1) }), [theme]);

    // Memoize menu item rendering
    const renderStringOptions = useMemo(
        () =>
            allOptions.map((selected) => (
                <MenuItem key={selected.id} value={selected.id} sx={{ alignItems: 'flex-start' }}>
                    <Checkbox checked={selected.isSelected} sx={checkboxStyles} />
                    <ListItemText
                        primary={selected.label}
                        secondary={selected.secondaryContent}
                        sx={{ textTransform: 'capitalize' }}
                        slotProps={{
                            primary: {
                                sx: { fontWeight: selected.primaryFontWeight ?? 500 }
                            },
                            secondary: {
                                component: 'div',
                                sx: { mt: 0.5 }
                            }
                        }}
                    />
                </MenuItem>
            )),
        [allOptions, checkboxStyles]
    );

    // Memoize helper function to convert IDs to options
    const idsToOptions = useCallback(
        (ids: string[]) => {
            return allOptions.filter((option) => ids.find((id) => option.id === id));
        },
        [allOptions]
    );

    // Memoize render value function
    const renderValue = useCallback(
        (selected: unknown) => {
            const selectedIDs = selected as string[];
            if (selectedIDs.length === 0) {
                return <LabelLarge>{placeHolder}</LabelLarge>;
            }
            const selectedItems = idsToOptions(selectedIDs);
            return (
                <FlexWrapper>
                    <Check sx={checkIconStyles} />
                    <EllipsisText>
                        {shouldKeepPlaceholder && `${placeHolder}: `}
                        {selectedItems.map((item) => capitalize(item.label)).join(', ')}
                    </EllipsisText>
                </FlexWrapper>
            );
        },
        [placeHolder, shouldKeepPlaceholder, idsToOptions, checkIconStyles]
    );

    // Memoize onChange handler
    const handleChange = useCallback(
        (event: SelectChangeEvent<unknown>) => {
            const selectedIDs = (event as SelectChangeEvent<string[]>).target.value as string[];
            onChange(selectedIDs);
        },
        [onChange]
    );

    // Memoize OutlinedInput component
    const inputComponent = useMemo(() => <OutlinedInput />, []);

    return (
        <>
            <InputLabel id={inputLabelId} sx={{ display: 'none' }}>
                {placeHolder}
            </InputLabel>
            <MultiSelectBaseSelect
                disabled={disabled}
                labelId={inputLabelId}
                name={name}
                isSelected={isSelected}
                multiple
                displayEmpty
                value={selectedOptions}
                noMaxWidth={noMaxWidth}
                onChange={handleChange}
                input={inputComponent}
                renderValue={renderValue}
            >
                {renderStringOptions}
            </MultiSelectBaseSelect>
        </>
    );
};

export { MultiSelect };
export type { MultiSelectProps };
